import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../components/shared/AuthContext';
import {
  PageLayout,
  PageHeader,
  Button,
  Card,
  StatCard,
  StatCardGrid,
  Table,
  Badge,
  TablePagination,
  Modal,
  SearchInput,
  Spinner
} from '../components/ui';
import FormInput from '../components/ui/FormInput';
import { EditIcon } from '../components/ui/Icons';
import { FaUsers, FaUniversity } from 'react-icons/fa';

// Helper functions to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp * 1000);
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
};

const formatMonthlyDue = (monthlyDue) => {
  if (!monthlyDue) return 'N/A';
  if (typeof monthlyDue === 'string' && monthlyDue.includes('-')) {
    const date = new Date(monthlyDue);
    return `Day ${date.getDate()}`;
  }
  const dayNumber = parseInt(monthlyDue);
  if (!isNaN(dayNumber)) {
    return `Day ${dayNumber}`;
  }
  return 'N/A';
};

const getTodayDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const ChitFund = () => {
  const { permissions, roleName, data } = useAuth();
  console.log("branchId:", data?.branchId);
  const branchId = data?.branchId || null;
  
  const [chitDetails, setChitDetails] = useState([]);
  const [chitStatements, setChitStatements] = useState([]);
  const [chitUsers, setChitUsers] = useState([]);
  const [personalDetails, setPersonalDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedChitId, setSelectedChitId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    monthlyDueDay: '',
    durationMonth: '',
    startDate: getTodayDate(),
    status: true,
  });
  const [monthlyDetails, setMonthlyDetails] = useState([]);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination & Filtering
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const filteredChitDetails = Array.isArray(chitDetails)
    ? chitDetails.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(item.id).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const indexOfLastRow = page * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredChitDetails.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil((filteredChitDetails?.length || 0) / rowsPerPage);

  // Filter customers based on search
  const filteredCustomers = personalDetails.filter(customer =>
    customer.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.customerUniqueId?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Fetch chit details with dynamic branchId filter
  useEffect(() => {
    const fetchChitDetails = async () => {
      if (!branchId) {
        setFormError('Branch ID is not available');
        setChitDetails([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/chit_details?filter={"branchId":${branchId}}`);
        let chitData = [];
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          chitData = response.data.data;
        } else if (Array.isArray(response.data)) {
          chitData = response.data;
        } else if (response.data && typeof response.data === 'object') {
          chitData = [response.data];
        }
        setChitDetails(chitData);
      } catch (error) {
        console.error('Error fetching chit details:', error.message);
        setFormError('Failed to load chit details');
        setChitDetails([]);
      } finally {
        setLoading(false);
      }
    };
    fetchChitDetails();
  }, [branchId]);

  useEffect(() => {
    const fetchPersonalDetails = async () => {
      if (!branchId) {
        console.log('Branch ID is not available for personal details');
        setPersonalDetails([]);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/personal_details?filter={"branchId":${branchId}}`);
        let customerData = [];
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          customerData = response.data.data;
        } else if (Array.isArray(response.data)) {
          customerData = response.data;
        } else if (response.data && typeof response.data === 'object') {
          customerData = [response.data];
        }
        console.log('Personal details fetched for branchId:', branchId, 'Count:', customerData.length);
        setPersonalDetails(customerData);
      } catch (error) {
        console.error('Error fetching personal details:', error.message);
        setPersonalDetails([]);
      }
    };
    fetchPersonalDetails();
  }, [branchId]);

  const fetchChitUsers = async () => {
    try {
      let url = `${API_BASE_URL}/api/getModelData/chit_user`;
      const response = await axios.get(url);
      const data = response.data;
      let chitUserData = [];
      if (data && data.data && Array.isArray(data.data)) {
        chitUserData = data.data;
      } else if (Array.isArray(data)) {
        chitUserData = data;
      } else if (data && typeof data === 'object') {
        chitUserData = [data];
      }
      setChitUsers(chitUserData);
    } catch (error) {
      console.error('Error fetching chit users:', error.message);
      setChitUsers([]);
    }
  };

  const fetchChitStatements = async () => {
    try {
      const response = await axios.get('/api/getModelData/chit_statements', {
        baseURL: API_BASE_URL,
      });

      const data = response.data;
      let statementsData = [];
      if (data && data.data && Array.isArray(data.data)) {
        statementsData = data.data;
      } else if (Array.isArray(data)) {
        statementsData = data;
      } else if (data && typeof data === 'object') {
        statementsData = [data];
      }
      setChitStatements(statementsData);
    } catch (error) {
      console.error('Error fetching chit statements:', error.message);
      setChitStatements([]);
    }
  };

  useEffect(() => {
    fetchChitStatements();
    fetchChitUsers();
  }, []);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'durationMonth') {
      const duration = parseInt(value) || 0;
      if (duration > 0) {
        const currentDetails = [...monthlyDetails];
        const newMonthlyDetails = Array.from({ length: duration }, (_, index) => {
          return currentDetails[index] || {
            month: index + 1,
            discountToken: '',
            paidBy: '',
            tokenGivenTo: '',
            token: '',
          };
        });
        setMonthlyDetails(newMonthlyDetails);
      } else {
        setMonthlyDetails([]);
      }
    }
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      title: '',
      amount: '',
      monthlyDueDay: '',
      durationMonth: '',
      startDate: getTodayDate(),
      status: true,
    });
    setMonthlyDetails([]);
    setFormError('');
    setFormSuccess('');
    setShowModal(true);
  };

  const handleManageCustomers = (chitId) => {
    setSelectedChitId(chitId);
    setCustomerSearch('');

    const existingCustomers = chitUsers
      .filter(cu => cu.chit_id == chitId)
      .map(cu => cu.user_id);

    setSelectedCustomers(existingCustomers);
    setShowCustomerModal(true);
  };

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const handleSaveCustomers = async () => {
    if (!selectedChitId) {
      setFormError('No chit selected');
      return;
    }

    setLoading(true);
    try {
      const accountResponse = await axios.get(`${API_BASE_URL}/api/getModelData/account_details`);
      let existingAccounts = [];
      if (accountResponse.data && accountResponse.data.data && Array.isArray(accountResponse.data.data)) {
        existingAccounts = accountResponse.data.data;
      } else if (Array.isArray(accountResponse.data)) {
        existingAccounts = accountResponse.data;
      } else if (accountResponse.data && typeof accountResponse.data === 'object') {
        existingAccounts = [accountResponse.data];
      }

      const existingChitUsers = chitUsers.filter(cu => cu.chit_id == selectedChitId);
      const existingUserIds = existingChitUsers.map(cu => cu.user_id);

      const usersToAdd = selectedCustomers.filter(userId => !existingUserIds.includes(userId));
      const usersToRemove = existingUserIds.filter(userId => !selectedCustomers.includes(userId));

      for (const userId of usersToAdd) {
        await axios.post('/api/addDetails', {
          model: 'chit_user',
          type: 'Add',
          chit_id: selectedChitId,
          user_id: userId,
          salary_Date: getTodayDate()
        }, { baseURL: API_BASE_URL });

        const customer = personalDetails.find(pd => pd.id === userId);
        if (customer && customer.customerUniqueId) {
          const existingAccount = existingAccounts.find(
            account => account.customerUniqueId === customer.customerUniqueId && account.accountType === '05'
          );

          if (!existingAccount) {
            const accountNumber = `${customer.customerUniqueId}-${Date.now()}`;
            await axios.post('/api/addDetails', {
              model: 'account_details',
              type: 'Add',
              customerUniqueId: customer.customerUniqueId,
              accountNumber: accountNumber,
              accountType: '05',
            }, { baseURL: API_BASE_URL });
          } else {
            console.log(`Account with type '05' already exists for customerUniqueId: ${customer.customerUniqueId}`);
          }
        } else {
          console.warn(`Customer with ID ${userId} not found or missing customerUniqueId`);
        }
      }

      for (const userId of usersToRemove) {
        const chitUserToRemove = existingChitUsers.find(cu => cu.user_id === userId);
        if (chitUserToRemove && chitUserToRemove.id) {
          await axios.post('/api/delDetails', {
            model: 'chit_user',
            id: chitUserToRemove.id
          }, { baseURL: API_BASE_URL });
        } else {
          console.warn(`Chit user with user_id ${userId} not found for chit_id ${selectedChitId}`);
        }
      }

      await fetchChitUsers();

      setFormSuccess('Customers and accounts updated successfully');
      setTimeout(() => {
        setShowCustomerModal(false);
        setFormSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error in handleSaveCustomers:', error);
      setFormError(error.response?.data?.message || error.message || 'Failed to update customers or accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (item) => {
    setIsEditMode(true);
    setEditingId(item.id);
    let startDateFormatted = getTodayDate();
    if (item.start_date) {
      const date = new Date(item.start_date * 1000);
      startDateFormatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    let monthlyDueDay = '';
    if (item.monthly_due) {
      if (typeof item.monthly_due === 'string' && item.monthly_due.includes('-')) {
        const date = new Date(item.monthly_due);
        monthlyDueDay = date.getDate().toString();
      } else {
        monthlyDueDay = item.monthly_due.toString();
      }
    }

    setFormData({
      title: item.title || '',
      amount: item.amount || '',
      monthlyDueDay: monthlyDueDay,
      durationMonth: item.duration_months || '',
      startDate: startDateFormatted,
      status: item.status === 1,
    });

    try {
      const chitStatementsForThisChit = chitStatements.filter(
        statement => statement.chit_id === item.id
      );

      if (item.duration_months) {
        const duration = parseInt(item.duration_months);
        const newMonthlyDetails = Array.from({ length: duration }, (_, index) => {
          const existingStatement = chitStatementsForThisChit.find(
            statement => statement.month_no === (index + 1)
          );

          return existingStatement ? {
            month: index + 1,
            discountToken: existingStatement.discountToken || '',
            paidBy: existingStatement.paidBy || '',
            tokenGivenTo: existingStatement.tokenGivenTo || '',
            token: existingStatement.token || '',
            id: existingStatement.id,
          } : {
            month: index + 1,
            discountToken: '',
            paidBy: '',
            tokenGivenTo: '',
            token: '',
          };
        });
        setMonthlyDetails(newMonthlyDetails);
      } else {
        setMonthlyDetails([]);
      }
    } catch (error) {
      console.error('Error loading chit statements:', error);
      if (item.duration_months) {
        const duration = parseInt(item.duration_months);
        const newMonthlyDetails = Array.from({ length: duration }, (_, index) => ({
          month: index + 1,
          discountToken: '',
          paidBy: '',
          tokenGivenTo: '',
          token: '',
        }));
        setMonthlyDetails(newMonthlyDetails);
      } else {
        setMonthlyDetails([]);
      }
    }

    setFormError('');
    setFormSuccess('');
    setShowModal(true);
  };

  const handleMonthlyDetailChange = (index, field, value) => {
    setMonthlyDetails((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setLoading(true);

    if (
      !formData.title ||
      !formData.amount ||
      !formData.monthlyDueDay ||
      !formData.durationMonth ||
      !formData.startDate
    ) {
      setFormError('All fields are required');
      setLoading(false);
      return;
    }

    if (
      isNaN(formData.amount) ||
      isNaN(formData.durationMonth) ||
      isNaN(formData.monthlyDueDay)
    ) {
      setFormError('Amount, Monthly Due Day, and Duration must be numbers');
      setLoading(false);
      return;
    }

    if (formData.monthlyDueDay < 1 || formData.monthlyDueDay > 31) {
      setFormError('Monthly Due Day must be between 1 and 31');
      setLoading(false);
      return;
    }

    if (!branchId) {
      setFormError('Branch ID is not available');
      setLoading(false);
      return;
    }

    try {
      const chitRequestData = {
        model: 'chit_details',
        type: isEditMode ? 'Edit' : 'Add',
        title: formData.title,
        amount: parseFloat(formData.amount),
        monthly_due: parseInt(formData.monthlyDueDay),
        duration_months: parseInt(formData.durationMonth),
        start_date: Math.floor(new Date(formData.startDate).getTime() / 1000),
        status: formData.status ? 1 : 0,
        branchId: branchId,
      };

      if (isEditMode && editingId) {
        chitRequestData.id = editingId;
      }

      const chitResponse = await axios.post('/api/addDetails', chitRequestData, {
        baseURL: API_BASE_URL
      });

      const chitId = isEditMode ? editingId : chitResponse.data?.id;

      if (chitId && monthlyDetails.length > 0) {
        for (const detail of monthlyDetails) {
          if (!detail.discountToken && !detail.paidBy && !detail.tokenGivenTo && !detail.token) {
            continue;
          }

          const statementRequestData = {
            model: 'chit_statements',
            type: (isEditMode && detail.id) ? 'Edit' : 'Add',
            chit_id: chitId,
            month_no: detail.month,
            discountToken: detail.discountToken || '',
            paidBy: detail.paidBy || '',
            tokenGivenTo: detail.tokenGivenTo || '',
            token: detail.token || '',
          };

          if (isEditMode && detail.id) {
            statementRequestData.id = detail.id;
          }

          await axios.post('/api/addDetails', statementRequestData, {
            baseURL: API_BASE_URL
          });
        }
      }

      setFormSuccess(isEditMode ? 'Chit fund updated successfully' : 'Chit fund added successfully');

      setFormData({
        title: '',
        amount: '',
        monthlyDueDay: '',
        durationMonth: '',
        startDate: getTodayDate(),
        status: true,
      });
      setMonthlyDetails([]);

      const response = await axios.get(`/api/getModelData/chit_details?filter={"branchId":${branchId}}`, {
        baseURL: API_BASE_URL,
      });

      const data = response.data;
      let chitData = [];
      if (data && data.data && Array.isArray(data.data)) {
        chitData = data.data;
      } else if (Array.isArray(data)) {
        chitData = data;
      } else if (data && typeof data === 'object') {
        chitData = [data];
      }
      setChitDetails(chitData);

      await fetchChitStatements();

      setTimeout(() => {
        setShowModal(false);
        setFormSuccess('');
        setIsEditMode(false);
        setEditingId(null);
      }, 2000);
    } catch (error) {
      setFormError(error.response?.data?.message || error.message || 'Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  // Get customer count for a chit
  const getCustomerCount = (chitId) => {
    return chitUsers.filter(cu => cu.chit_id == chitId).length;
  };

  // Get customer names for a chit
  const getCustomerNames = (chitId) => {
    const chitUserIds = chitUsers
      .filter(cu => cu.chit_id == chitId)
      .map(cu => cu.user_id);

    return personalDetails
      .filter(pd => chitUserIds.includes(pd.id))
      .map(pd => pd.name || 'N/A')
      .slice(0, 3);
  };

  return (
    <PageLayout>
      <PageHeader
        title="Chit Fund Management"
        actions={
          <Button
            variant="primary"
            onClick={handleAdd}
            startIcon={<FaUniversity className="w-4 h-4" />}
          >
            Add Chit Fund
          </Button>
        }
      />

      {formError && (
        <div className="flex items-center justify-between gap-3 p-4 mb-6 text-sm font-semibold text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-xl">
          <span>{formError}</span>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setFormError("");
              window.location.reload();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <StatCardGrid cols={3} className="mb-6">
        <StatCard
          title="Total Chits"
          value={chitDetails.length}
          accent="primary"
          accentBar
          icon={<FaUniversity className="w-5 h-5 text-[#004BB5]" />}
        />
        <StatCard
          title="Active Chits"
          value={chitDetails.filter((item) => item.status === 1).length}
          accent="success"
          accentBar
          icon={<FaUniversity className="w-5 h-5 text-[#10B981]" />}
        />
        <StatCard
          title="Inactive Chits"
          value={chitDetails.filter((item) => item.status === 0).length}
          accent="danger"
          accentBar
          icon={<FaUniversity className="w-5 h-5 text-[#EF4444]" />}
        />
      </StatCardGrid>

      {/* Chit Details Section */}
      <Card className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h2 className="text-lg font-bold text-[#0F172A]">Chit Details</h2>
          <div className="w-full md:w-80">
            <SearchInput
              placeholder="Search by title or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table wrapper */}
        <Table>
          <Table.Head>
            <tr>
              <Table.HeadCell>S.No</Table.HeadCell>
              <Table.HeadCell>ID</Table.HeadCell>
              <Table.HeadCell>Title</Table.HeadCell>
              <Table.HeadCell align="right">Amount (₹)</Table.HeadCell>
              <Table.HeadCell>Monthly Due</Table.HeadCell>
              <Table.HeadCell>Duration (Months)</Table.HeadCell>
              <Table.HeadCell>Start Date</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell>Customers</Table.HeadCell>
              <Table.HeadCell align="center">Actions</Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <Table.Cell colSpan={10} align="center" className="py-12 text-[#64748B]">
                  <div className="flex items-center justify-center gap-2">
                    <Spinner size="sm" />
                    <span>Loading chit details...</span>
                  </div>
                </Table.Cell>
              </tr>
            ) : currentRows.length > 0 ? (
              currentRows.map((item, index) => (
                <Table.Row key={item.id}>
                  <Table.Cell>{indexOfFirstRow + index + 1}</Table.Cell>
                  <Table.Cell className="font-semibold text-primary">{item.id}</Table.Cell>
                  <Table.Cell className="font-medium text-[#0F172A]">{item.title || 'N/A'}</Table.Cell>
                  <Table.AmountCell align="right">
                    {parseFloat(item.amount || 0)}
                  </Table.AmountCell>
                  <Table.Cell>{formatMonthlyDue(item.monthly_due)}</Table.Cell>
                  <Table.Cell>{item.duration_months || 'N/A'}</Table.Cell>
                  <Table.Cell>{formatTimestamp(item.start_date)}</Table.Cell>
                  <Table.Cell>
                    <Badge variant={item.status === 1 ? 'success' : 'danger'} dot>
                      {item.status === 1 ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="info" size="sm">
                        {getCustomerCount(item.id)} customers
                      </Badge>
                      {getCustomerNames(item.id).map((name, idx) => (
                        <span key={idx} className="text-xs text-[#64748B]">
                          {name}
                        </span>
                      ))}
                      {getCustomerCount(item.id) > 3 && (
                        <span className="text-xs text-[#94A3B8] italic">
                          ...and {getCustomerCount(item.id) - 3} more
                        </span>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.ActionCell align="center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => handleEdit(item)}
                        disabled={loading}
                        className="p-1.5 text-[#004BB5] hover:bg-[#E8F0FE] rounded-lg transition-colors duration-150 disabled:opacity-50"
                        title="Edit"
                      >
                        <EditIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleManageCustomers(item.id)}
                        disabled={loading}
                        className="p-1.5 text-[#10B981] hover:bg-[#ECFDF5] rounded-lg transition-colors duration-150 disabled:opacity-50"
                        title="Manage Customers"
                      >
                        <FaUsers className="w-4 h-4" />
                      </button>
                    </div>
                  </Table.ActionCell>
                </Table.Row>
              ))
            ) : (
              <Table.EmptyRow colSpan={10} message="No chit fund records found" />
            )}
          </Table.Body>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <TablePagination
            currentPage={page}
            totalPages={totalPages}
            totalEntries={filteredChitDetails.length}
            itemsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
          />
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditMode ? 'Edit Chit Fund' : 'Add New Chit Fund'}
        size="2xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
              isLoading={loading}
            >
              {isEditMode ? 'Update Chit Fund' : 'Add Chit Fund'}
            </Button>
          </>
        }
      >
        {formError && (
          <div className="p-4 mb-4 text-sm font-semibold text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-xl">
            {formError}
          </div>
        )}
        {formSuccess && (
          <div className="p-4 mb-4 text-sm font-semibold text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl">
            {formSuccess}
          </div>
        )}

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-bold text-[#0F172A] mb-4">Basic Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                placeholder="Enter chit fund title"
                required
              />
              <FormInput
                label="Amount (₹)"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleFormChange}
                placeholder="Enter amount"
                required
                min="1"
              />
              <FormInput
                label="Monthly Due Day"
                name="monthlyDueDay"
                type="number"
                value={formData.monthlyDueDay}
                onChange={handleFormChange}
                placeholder="Enter day (1-31)"
                required
                min="1"
                max="31"
              />
              <FormInput
                label="Duration (Months)"
                name="durationMonth"
                type="number"
                value={formData.durationMonth}
                onChange={handleFormChange}
                placeholder="Enter duration"
                required
                min="1"
                max="60"
              />
              <div className="md:col-span-2">
                <FormInput
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="md:col-span-2 flex items-center pt-2">
                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="status"
                    checked={formData.status}
                    onChange={handleFormChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-[#E2E8F0] rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-[#CBD5E1] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#004BB5]"></div>
                  <span className="ml-3 text-sm font-semibold text-[#1E293B]">Active Status</span>
                </label>
              </div>
            </div>
          </div>

          {monthlyDetails.length > 0 && (
            <div className="border border-[#E2E8F0] rounded-xl overflow-hidden mt-6">
              <div className="px-5 py-4 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <h3 className="text-sm font-bold text-[#0F172A]">
                  Monthly Statements ({monthlyDetails.length} Months)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-2 text-[11px] font-semibold text-[#475569] uppercase tracking-[0.06em] text-left">Month</th>
                      <th className="px-4 py-2 text-[11px] font-semibold text-[#475569] uppercase tracking-[0.06em] text-left">Discount Token</th>
                      <th className="px-4 py-2 text-[11px] font-semibold text-[#475569] uppercase tracking-[0.06em] text-left">Paid By</th>
                      <th className="px-4 py-2 text-[11px] font-semibold text-[#475569] uppercase tracking-[0.06em] text-left">Token Given To</th>
                      <th className="px-4 py-2 text-[11px] font-semibold text-[#475569] uppercase tracking-[0.06em] text-left">Token</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyDetails.map((detail, index) => (
                      <tr key={index} className="border-b border-[#F1F5F9] last:border-0">
                        <td className="px-4 py-2.5 font-semibold text-[#1E293B]">
                          Month {detail.month}
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            placeholder="Discount token"
                            value={detail.discountToken}
                            onChange={(e) => handleMonthlyDetailChange(index, 'discountToken', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-[#1E293B]"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            placeholder="Paid by"
                            value={detail.paidBy}
                            onChange={(e) => handleMonthlyDetailChange(index, 'paidBy', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-[#1E293B]"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            placeholder="Token given to"
                            value={detail.tokenGivenTo}
                            onChange={(e) => handleMonthlyDetailChange(index, 'tokenGivenTo', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-[#1E293B]"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            placeholder="Token"
                            value={detail.token}
                            onChange={(e) => handleMonthlyDetailChange(index, 'token', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-[#1E293B]"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4 bg-[#F8FAFC] border-t border-[#E2E8F0]">
                <p className="text-xs text-[#64748B] font-medium">
                  ℹ️ You can fill these details now or update them later. Leave blank for future updates.
                </p>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Customer Management Modal */}
      <Modal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        title="Manage Customers"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCustomerModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleSaveCustomers}
              disabled={loading}
              isLoading={loading}
            >
              Save Customers
            </Button>
          </>
        }
      >
        {formError && (
          <div className="p-4 mb-4 text-sm font-semibold text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-xl">
            {formError}
          </div>
        )}
        {formSuccess && (
          <div className="p-4 mb-4 text-sm font-semibold text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl">
            {formSuccess}
          </div>
        )}

        <div className="space-y-6">
          <div>
            <SearchInput
              placeholder="Search customers by name or ID..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
            />
          </div>

          <div>
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">
              Select Customers ({selectedCustomers.length} selected)
            </h3>

            <div className="max-h-[400px] overflow-y-auto pr-1 space-y-2">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer) => {
                  const isChecked = selectedCustomers.includes(customer.id);
                  return (
                    <label
                      key={customer.id}
                      className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all duration-150 select-none
                        ${isChecked
                          ? "border-[#10B981] bg-[#ECFDF5]/30 hover:bg-[#ECFDF5]/50"
                          : "border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCustomerSelect(customer.id)}
                        className="w-4 h-4 rounded border-[#CBD5E1] text-[#10B981] focus:ring-[#10B981]/30 accent-[#10B981] cursor-pointer"
                      />
                      <div className="flex-grow">
                        <p className={`text-sm font-semibold transition-colors ${isChecked ? "text-[#065F46]" : "text-[#1E293B]"}`}>
                          {customer.name || 'N/A'}
                        </p>
                        <p className="text-xs text-[#64748B]">
                          ID: {customer.customerUniqueId || 'N/A'}
                        </p>
                      </div>
                    </label>
                  );
                })
              ) : (
                <div className="text-center py-8 text-[#94A3B8] italic">
                  No customers found
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
};

export default ChitFund;